import { Box, Stack } from '@chakra-ui/react';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { NavigationButtons } from '~/components/navigation/NavigationButtons';
import {
  getProjectRoute,
  PROJECT_ROUTE_CONFIG,
  type PROJECT_ROUTES,
} from '~/pages/project/projectRoutes';
import { PageHeader } from './PageHeader';

interface ProjectPageTemplateProps {
  children: ReactNode;
  title: string;
  onBack?: () => void;
  onContinue?: () => void;
  backLabel?: string;
  continueLabel?: string;
}

export const ProjectPageTemplate = ({
  children,
  title,
  onBack,
  onContinue,
  backLabel,
  continueLabel,
}: ProjectPageTemplateProps) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();

  // Find the current route by matching the full path
  const currentRoute = Object.entries(PROJECT_ROUTE_CONFIG).find(([, config]) =>
    location.pathname.endsWith(config.path)
  )?.[0] as keyof typeof PROJECT_ROUTES | undefined;

  // Auto-determine backLabel from route config if not provided
  const resolvedBackLabel = useMemo(() => {
    if (backLabel) {
      return backLabel;
    }

    if (currentRoute && PROJECT_ROUTE_CONFIG[currentRoute].prevRoute) {
      const prevRoute = PROJECT_ROUTE_CONFIG[currentRoute].prevRoute;
      if (prevRoute) {
        const prevRouteConfig = PROJECT_ROUTE_CONFIG[prevRoute];
        return `Back to ${prevRouteConfig.title}`;
      }
    }

    return 'Back to Home';
  }, [backLabel, currentRoute]);

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }

    if (currentRoute && PROJECT_ROUTE_CONFIG[currentRoute].prevRoute) {
      const prevRoute = PROJECT_ROUTE_CONFIG[currentRoute].prevRoute;
      if (prevRoute) {
        navigate(getProjectRoute(id!, prevRoute));
      }
    } else {
      navigate('/');
    }
  };

  const handleContinue = () => {
    if (onContinue) {
      onContinue();
      return;
    }

    if (!currentRoute) {
      return;
    }

    const routeConfig = PROJECT_ROUTE_CONFIG[currentRoute];

    if (!routeConfig.nextRoute) {
      return;
    }

    const nextRoute = routeConfig.nextRoute;
    const newPath = getProjectRoute(id!, nextRoute);
    navigate(newPath);
  };

  return (
    <Stack direction="column" w="full" gap={0} pb={8}>
      {/* Header with project name and navigation buttons */}
      <PageHeader
        title={title}
        onBack={handleBack}
        onContinue={handleContinue}
        backLabel={resolvedBackLabel}
        continueLabel={continueLabel}
      />

      {/* Page content */}
      <Box px={6}>{children}</Box>

      {/* Footer navigation buttons */}
      <Box px={6} mt={6} display="flex" justifyContent="flex-end">
        <NavigationButtons
          onBack={handleBack}
          onContinue={handleContinue}
          backLabel={resolvedBackLabel}
          continueLabel={continueLabel}
        />
      </Box>
    </Stack>
  );
};
